import { Check, Copy, KeyRound, Plus } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'

import { DataTable, type Column } from '@/components/data/DataTable'
import { DetailPane, KeyValue, detailActionClass, detailDangerClass } from '@/components/data/DetailPane'
import { FilterBar } from '@/components/data/FilterBar'
import { SplitView } from '@/components/data/SplitView'
import { Page } from '@/components/layout/Page'
import { SettingsField, SettingsStatus, SettingsTabs, settingsPrimaryClass } from '@/components/SettingsTabs'
import {
  fetchMcpTokens,
  mintMcpToken,
  revokeMcpToken,
  type McpToken,
  type MintedMcpToken,
} from '@/lib/mcpTokens'
import { useSelection } from '@/lib/useSelection'

type ProfileOption = 'full' | 'read_only' | 'single_domain'

const NEW = 'new'

function profileLabel(profile: string): string {
  if (profile === 'full') return 'full'
  if (profile === 'read_only') return 'read only'
  if (profile.startsWith('domain:')) return `domain: ${profile.slice(7)}`
  return profile
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : 'never')

const COLUMNS: Column<McpToken>[] = [
  { key: 'label', header: 'Label', mono: true },
  { key: 'profile', header: 'Profile', width: '18%', render: (t) => profileLabel(t.profile ?? 'full'), title: (t) => t.profile },
  { key: 'created_at', header: 'Created', mono: true, width: '200px', render: (t) => when(t.created_at) },
  { key: 'last_used_at', header: 'Last used', mono: true, width: '200px', render: (t) => when(t.last_used_at) },
]

/** One-time reveal of a freshly minted token — the server stores only its hash. */
function Reveal({ minted, onDismiss }: { minted: MintedMcpToken; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    void navigator.clipboard.writeText(minted.token).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    })
  }
  return (
    <div className="space-y-2 rounded-md border border-warn px-3 py-2.5">
      <div className="text-[12px] font-medium text-warn">Token “{minted.label}” — shown once</div>
      <p className="text-[12px] text-text-2">Copy it now. It is never displayed again — only its hash is stored.</p>
      <div className="flex items-stretch gap-2">
        <code className="min-w-0 flex-1 truncate rounded-md bg-surface px-2.5 py-1.5 font-mono text-[12px] text-foreground" title={minted.token}>
          {minted.token}
        </code>
        <button type="button" onClick={copy} className={detailActionClass}>
          {copied ? <Check aria-hidden className="size-3.5 text-ok" /> : <Copy aria-hidden className="size-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <button type="button" onClick={onDismiss} className="text-[12px] text-text-2 underline underline-offset-2 hover:text-foreground">
        I’ve copied it — dismiss
      </button>
    </div>
  )
}

function NewTokenForm({ onMinted }: { onMinted: (m: MintedMcpToken) => void }) {
  const [label, setLabel] = useState('')
  const [profile, setProfile] = useState<ProfileOption>('full')
  const [domainName, setDomainName] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  async function submit(e: FormEvent) {
    e.preventDefault()
    setMsg(null)
    const resolved = profile === 'single_domain' ? `domain:${domainName.trim()}` : profile
    try {
      onMinted(await mintMcpToken(label.trim(), resolved))
      setLabel('')
      setProfile('full')
      setDomainName('')
    } catch (err) {
      setMsg(`Error: ${(err as Error).message}`)
    }
  }
  return (
    <form onSubmit={submit} className="space-y-3">
      <SettingsField id="mcp-label" label="Label" placeholder="e.g. laptop, ci-runner" value={label} onChange={setLabel} />
      <div className="space-y-1">
        <label htmlFor="mcp-profile" className="block font-mono text-[11px] text-text-3">Profile</label>
        <select
          id="mcp-profile"
          value={profile}
          onChange={(e) => setProfile(e.target.value as ProfileOption)}
          className="h-8 w-full rounded-md border border-input bg-surface px-2 text-[12.5px] text-foreground"
        >
          <option value="full">full</option>
          <option value="read_only">read only</option>
          <option value="single_domain">single domain</option>
        </select>
      </div>
      {profile === 'single_domain' && (
        <SettingsField id="mcp-domain" label="Domain" placeholder="e.g. users, projects, issues" value={domainName} onChange={setDomainName} />
      )}
      <button type="submit" className={settingsPrimaryClass}>
        <KeyRound aria-hidden className="size-3.5" /> Generate
      </button>
      {msg && <SettingsStatus msg={msg} />}
    </form>
  )
}

export default function SettingsMcpTokens() {
  const [tokens, setTokens] = useState<McpToken[] | null>(null)
  const [minted, setMinted] = useState<MintedMcpToken | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [sel, setSel] = useSelection()

  const refresh = () => fetchMcpTokens()
    .then((t) => setTokens(Array.isArray(t) ? t : []))
    .catch(() => setTokens([]))
  useEffect(() => {
    void refresh()
  }, [])

  async function revoke(id: string) {
    setMsg(null)
    try {
      await revokeMcpToken(id)
    } catch (err) {
      // keep the token selected: it is still there
      setMsg(`Error: ${(err as Error).message}`)
      void refresh()
      return
    }
    if (minted?.id === id) setMinted(null)
    setMsg('Token revoked.')
    setSel(null)
    void refresh()
  }

  const list = tokens ?? []
  const selected = list.find((t) => t.id === sel) ?? null

  const detail = sel === NEW
    ? (
      <DetailPane eyebrow="MCP token" title="New token" status={<span className="text-text-2">Authenticates an HTTP MCP client as you</span>}>
        {minted && <Reveal minted={minted} onDismiss={() => setMinted(null)} />}
        <NewTokenForm onMinted={(m) => { setMinted(m); setMsg(`Token “${m.label}” created.`); void refresh() }} />
      </DetailPane>
    )
    : selected && (
      <DetailPane
        eyebrow="MCP token"
        title={selected.label}
        status={<span className="text-text-2">{profileLabel(selected.profile ?? 'full')}</span>}
        actions={<button type="button" className={detailDangerClass} onClick={() => void revoke(selected.id)}>Revoke</button>}
      >
        <KeyValue
          items={[
            ['Profile', profileLabel(selected.profile ?? 'full')],
            ['Created', <span className="font-mono">{when(selected.created_at)}</span>],
            ['Last used', <span className="font-mono">{when(selected.last_used_at)}</span>],
            ['Id', <span className="font-mono text-text-2">{selected.id}</span>],
          ]}
        />
        <p className="text-[12px] text-text-3">The token itself is stored as a hash and cannot be shown again.</p>
      </DetailPane>
    )

  return (
    <Page
      title="Settings"
      hint="Bearer tokens that authenticate HTTP MCP clients as you (active when MCP_AUTH_ENABLED is set). Stored as a hash — shown once at creation."
      context={<SettingsTabs />}
      actions={(
        <button type="button" className={settingsPrimaryClass} onClick={() => setSel(NEW)}>
          <Plus aria-hidden className="size-3.5" /> New token
        </button>
      )}
    >
      <FilterBar>
        <span className="font-mono text-[11px] text-text-3">
          {tokens === null ? 'loading…' : `${list.length} active token${list.length === 1 ? '' : 's'}`} · stored as a hash, shown once
        </span>
        {msg && <span className="ml-auto"><SettingsStatus msg={msg} /></span>}
      </FilterBar>
      <SplitView
        onCloseDetail={() => setSel(null)}
        emptyDetail="Select a token to see it or revoke it, or create a new one."
        detail={detail || null}
        list={(
          <DataTable
            label="MCP tokens"
            rows={list}
            columns={COLUMNS}
            rowId={(t) => t.id}
            selectedId={sel}
            onSelect={setSel}
            empty={tokens === null ? 'Loading…' : 'No MCP tokens yet.'}
          />
        )}
      />
    </Page>
  )
}
